import { useEffect, useRef } from 'react';
import { useMetatagsGetActive } from '@/core/api/generated/meta-tags/meta-tags';
import { QUERY_CONFIG } from '@/core/config';
import type { MetatagsGetActive200Item } from '@/core/api/generated/nxt48.schemas';

/**
 * Component that injects active meta tags and scripts into the DOM
 * Mounts in App.tsx to be present on all pages
 */
export function MetaTagsInjector() {
  const { data: activeMetaTags, error, isLoading } = useMetatagsGetActive({
    query: {
      ...QUERY_CONFIG.STANDARD,
      retry: 1,
    },
  });
  
  const injectedRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // No hacer nada si hay error o está cargando
    if (error || isLoading || !activeMetaTags || activeMetaTags.length === 0) {
      return;
    }
    
    try {
      const head = document.head;
      const body = document.body;
      const currentlyInjected = new Set<string>();
      
      (activeMetaTags as MetatagsGetActive200Item[]).forEach((tag) => {
        if (!tag.isActive) return;
        
        const id = `meta-tag-${tag.id}`;
        
        // Skip if already injected (avoid duplicates)
        if (injectedRef.current.has(id)) {
          currentlyInjected.add(id);
          return;
        }
        
        const target = tag.position === 'body' ? body : head;
        
        if (tag.type === 'meta') {
          // Create meta tag
          if (tag.property && tag.content) {
            const meta = document.createElement('meta');
            meta.setAttribute('property', tag.property);
            meta.setAttribute('content', tag.content);
            meta.setAttribute('data-meta-tag-id', id);
            target.appendChild(meta);
            currentlyInjected.add(id);
          }
        } else if (tag.type === 'script') {
          // Create script tag
          const script = document.createElement('script');
          
          if (tag.scriptSrc) {
            script.src = tag.scriptSrc;
          }
          
          if (tag.scriptContent) {
            script.textContent = tag.scriptContent;
          }
          
          if (tag.scriptType) {
            script.type = tag.scriptType;
          }
          
          if (tag.scriptAsync) {
            script.async = true;
          }
          
          if (tag.scriptDefer) {
            script.defer = true;
          }
          
          script.setAttribute('data-meta-tag-id', id);
          target.appendChild(script);
          currentlyInjected.add(id);
        }
      });
      
      // Cleanup: remove tags that are no longer active
      injectedRef.current.forEach((id) => {
        if (!currentlyInjected.has(id)) {
          const element = document.querySelector(`[data-meta-tag-id="${id}"]`);
          if (element) {
            element.remove();
          }
        }
      });
      
      injectedRef.current = currentlyInjected;
    } catch (err) {
      // Silently fail - meta tags injection should not break the app
      console.warn('Failed to inject meta tags:', err);
    }
  }, [activeMetaTags, error, isLoading]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      injectedRef.current.forEach((id) => {
        const element = document.querySelector(`[data-meta-tag-id="${id}"]`);
        if (element) {
          element.remove();
        }
      });
      injectedRef.current.clear();
    };
  }, []);
  
  return null; // This component doesn't render anything
}










